import darkSvg from './dark.svg';
import lightSvg from './light.svg';
import { useState, type MouseEvent } from 'react';
import { formatUnits } from 'viem';
import { ArrowUpDown } from 'lucide-react';
import { useProfile } from '@/routes/Providers/ProfileProvider';
import { track } from '@/shared/utils/track';
import { formatPrice } from '@/modules/format';
import { useSanBalance } from '@/modules/use-san-balance';
import { useWalletActionGuard } from '@/shared/utils/useWalletActionGuard';
import { useColorTheme } from '@/ColorThemeProvider';
import SmartLink from '@/shared/ui/SmartLink';
import Notifications from './Notifications';
import Button from '@/shared/ui/Button';
import { useGatedNav } from '@/shared/utils/useGatedNav';
import { zHeaderNotifications, zMobileTopBar } from '@/shared/constants/zIdxs';
import { useIsMobile } from '@/modules/use-window-width';
import { useNewOpenMarketsCount } from '@/shared/utils/useNewOpenMarketsCount';
import { useRunWhenReady } from '@/modules/use-run-when-ready';
import SwapModal from './SwapModal';
import ProfileMenu from './ProfileMenu';
import ScoreOgModal from './ScoreOgModal';
import { isDemoAccount } from '@/modules/demo-account';

const NAV_LABELS = {
  markets: 'Markets',
  arena: 'Arena',
  community: 'Community',
};

// `label` is the analytics and onboarding-anchor constant; the shown text
// is the nav key's translation.
const links = [
  { key: 'markets', label: 'Markets', to: '/markets' },
  { key: 'arena', label: 'Arena', to: '/arena' },
  { key: 'community', label: 'Community', to: '/community' },
] as const;

const NAV_LINK_CLASS =
  'flex h-8 items-center justify-center px-m rounded-xs text-body-md font-medium text-gray whitespace-nowrap aria-[current]:text-purple-default hover:text-purple-hover';

export default function Header() {
  const { profile, login, isAuthReady } = useProfile();
  const { pending: signInPending, run: runSignIn } = useRunWhenReady(
    isAuthReady,
    login,
  );
  const { switchColorTheme, isDark } = useColorTheme();
  const isMobile = useIsMobile();
  const [swapOpen, setSwapOpen] = useState(false);
  const { onNavClick, gateEl } = useGatedNav('header');
  const [scoreOgOpen, setScoreOgOpen] = useState(false);
  const newMarketsCount = useNewOpenMarketsCount();
  const { balance: sanBalance } = useSanBalance(profile?.address ?? undefined);
  const { guardWalletAction, walletGuardModal } = useWalletActionGuard(
    profile?.address ?? null,
  );

  const loginButtonEl = !profile && (
    <Button
      variant="primary"
      size={!isMobile ? 'lg' : 'sm'}
      loading={signInPending}
      onClick={() => {
        track('market_signin_clicked', { source: 'header' });
        runSignIn();
      }}
    >
      Sign In
    </Button>
  );

  // Score Arena lockup inlined as JSX — an <img> SVG can't theme-flip.
  // The wordmark is the export's outlined glyphs (Proxima Nova isn't
  // loaded as a font); its #15181F fill and the mark's dark half map to
  // fill-gray-text (white in dark mode, the landing's dark-bg logo
  // treatment), the mark's #7C5EF6 brand purple is theme-constant.
  const logoEl = (
    <SmartLink className="flex items-center" to="/" aria-label="Score Arena">
      <svg
        width={129}
        height={32}
        viewBox="0 0 129 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.84625 9.88225C5.79933 5.60775 11.6599 4.53546 15.9363 7.48723C20.2127 10.439 21.2854 16.2971 18.3323 20.5716C16.1574 23.7197 12.4054 25.1308 8.87064 24.4767L12.7626 20.0079C12.0623 19.8725 11.3712 19.6557 10.703 19.3552L6.79899 23.8376C6.26326 23.6013 5.74194 23.3115 5.2423 22.9666C5.18246 22.9253 5.12326 22.8833 5.06467 22.8409L12.966 13.7685C12.3661 13.3225 11.834 12.8174 11.3721 12.267L3.45252 21.3604C3.00136 20.8366 2.61482 20.2703 2.29523 19.6736L6.22843 15.1575C5.87721 14.4898 5.6092 13.7849 5.42755 13.0594L1.48118 17.5905C0.819299 15.0377 1.22939 12.2226 2.84625 9.88225Z"
          fill="#7C5EF6"
        />
        <path
          d="M5.9028 7.06805C8.94911 5.3227 12.854 5.35984 15.936 7.48722C19.6865 10.076 20.9726 14.9001 19.2402 18.931C17.2863 20.0504 14.9794 20.4367 12.7628 20.0084L12.7634 20.008C12.0669 19.8733 11.3793 19.658 10.7145 19.3599L10.7142 19.3601C10.7104 19.3584 10.7065 19.3568 10.7027 19.3551L6.79864 23.8378C6.80249 23.8395 6.8064 23.8411 6.81025 23.8428L1.60465 29.8203C1.13265 29.3242 0.722715 28.7844 0.377051 28.2122L5.05846 22.8367C5.06043 22.8381 5.06235 22.8396 5.06432 22.841L12.1516 14.7031C12.1519 14.7032 12.1522 14.7033 12.1524 14.7034L12.9668 13.7684C12.3669 13.3225 11.8348 12.8174 11.3729 12.267L11.3723 12.2675C11.3722 12.2673 11.372 12.2671 11.3719 12.2669L7.37491 16.8563C7.3723 16.8532 7.36971 16.85 7.36709 16.847L1.15242 23.9828C0.702552 23.4564 0.317648 22.8875 0 22.2882L2.28794 19.6611C2.29022 19.6653 2.29243 19.6697 2.29472 19.6739L6.22824 15.1574C6.22597 15.1531 6.22372 15.1487 6.22146 15.1444L6.22215 15.1437C5.87448 14.4801 5.60878 13.7799 5.42839 13.0594L5.42758 13.0602C5.4275 13.0599 5.42743 13.0596 5.42735 13.0593L5.42666 13.0598C4.93631 11.1017 5.07492 8.99431 5.9028 7.06805Z"
          className="fill-gray-text"
        />
        <path
          d="M28.7058 19.496L29.7458 18.056C30.4818 18.84 31.6338 19.528 33.1218 19.528C34.6578 19.528 35.2658 18.776 35.2658 18.072C35.2658 17.112 34.1298 16.824 32.8338 16.488C31.0898 16.056 29.0418 15.544 29.0418 13.288C29.0418 11.528 30.5938 10.168 32.9298 10.168C34.5938 10.168 35.9378 10.696 36.9298 11.656L35.8578 13.048C35.0258 12.216 33.8898 11.832 32.7698 11.832C31.6658 11.832 30.9618 12.36 30.9618 13.16C30.9618 13.992 32.0498 14.264 33.3138 14.584C35.0738 15.032 37.1698 15.576 37.1698 17.896C37.1698 19.672 35.9218 21.192 33.0418 21.192C31.0738 21.192 29.6498 20.504 28.7058 19.496ZM38.3658 17.128C38.3658 14.792 40.0138 13.08 42.3658 13.08C43.8698 13.08 44.7818 13.72 45.3258 14.456L44.2218 15.48C43.7898 14.872 43.1978 14.568 42.4458 14.568C41.0378 14.568 40.1098 15.624 40.1098 17.128C40.1098 18.632 41.0378 19.704 42.4458 19.704C43.1978 19.704 43.7898 19.384 44.2218 18.792L45.3258 19.816C44.7818 20.552 43.8698 21.192 42.3658 21.192C40.0138 21.192 38.3658 19.48 38.3658 17.128ZM50.2869 21.192C47.8389 21.192 46.3189 19.352 46.3189 17.128C46.3189 14.92 47.8389 13.08 50.2869 13.08C52.7669 13.08 54.2709 14.92 54.2709 17.128C54.2709 19.352 52.7669 21.192 50.2869 21.192ZM50.2869 19.704C51.7269 19.704 52.5269 18.504 52.5269 17.128C52.5269 15.768 51.7269 14.568 50.2869 14.568C48.8629 14.568 48.0629 15.768 48.0629 17.128C48.0629 18.504 48.8629 19.704 50.2869 19.704ZM55.9708 21V13.272H57.6508V14.408C58.2268 13.672 59.1548 13.096 60.1628 13.096V14.76C60.0188 14.728 59.8428 14.712 59.6348 14.712C58.9308 14.712 57.9868 15.192 57.6508 15.736V21H55.9708ZM61.0377 17.128C61.0377 14.888 62.6697 13.08 64.9577 13.08C67.2777 13.08 68.7657 14.856 68.7657 17.288V17.688H62.7977C62.9257 18.856 63.8057 19.816 65.2617 19.816C66.0137 19.816 66.9097 19.512 67.4537 18.968L68.2217 20.072C67.4537 20.808 66.3177 21.192 65.0857 21.192C62.7657 21.192 61.0377 19.576 61.0377 17.128ZM64.9577 14.456C63.5177 14.456 62.8617 15.56 62.7817 16.504H67.1337C67.1017 15.592 66.4777 14.456 64.9577 14.456ZM82.1333 21L81.3493 18.952H76.2933L75.5093 21H73.4613L77.6533 10.328H79.9893L84.1813 21H82.1333ZM78.8213 11.912L76.8213 17.304H80.8213L78.8213 11.912ZM85.2989 21V13.272H86.9789V14.408C87.5549 13.672 88.4829 13.096 89.4909 13.096V14.76C89.3469 14.728 89.1709 14.712 88.9629 14.712C88.2589 14.712 87.3149 15.192 86.9789 15.736V21H85.2989ZM90.3658 17.128C90.3658 14.888 91.9978 13.08 94.2858 13.08C96.6058 13.08 98.0938 14.856 98.0938 17.288V17.688H92.1258C92.2538 18.856 93.1338 19.816 94.5898 19.816C95.3418 19.816 96.2378 19.512 96.7818 18.968L97.5498 20.072C96.7818 20.808 95.6458 21.192 94.4138 21.192C92.0938 21.192 90.3658 19.576 90.3658 17.128ZM94.2858 14.456C92.8458 14.456 92.1898 15.56 92.1098 16.504H96.4618C96.4298 15.592 95.8058 14.456 94.2858 14.456ZM104.968 21V16.168C104.968 14.968 104.36 14.568 103.416 14.568C102.552 14.568 101.816 15.08 101.432 15.608V21H99.7521V13.272H101.432V14.328C101.944 13.72 102.936 13.08 104.152 13.08C105.816 13.08 106.648 13.976 106.648 15.56V21H104.968ZM113.55 21V20.168C112.974 20.824 112.078 21.192 111.054 21.192C109.79 21.192 108.366 20.344 108.366 18.632C108.366 16.856 109.79 16.12 111.054 16.12C112.094 16.12 112.99 16.456 113.55 17.112V15.96C113.55 15.064 112.814 14.52 111.742 14.52C110.878 14.52 110.126 14.84 109.454 15.496L108.766 14.328C109.662 13.48 110.782 13.08 112.014 13.08C113.726 13.08 115.23 13.8 115.23 15.88V21H113.55ZM113.55 19.24V18.072C113.166 17.544 112.446 17.272 111.71 17.272C110.766 17.272 110.062 17.816 110.062 18.664C110.062 19.496 110.766 20.04 111.71 20.04C112.446 20.04 113.166 19.768 113.55 19.24Z"
          className="fill-gray-text"
        />
      </svg>
    </SmartLink>
  );

  const iconUrl = JSON.stringify(!isDark ? darkSvg : lightSvg);
  const maskValue = `url(${iconUrl}) no-repeat center / contain`;
  const colorThemeButtonEl = (
    <button
      type="button"
      className="btn-reset flex size-8 items-center justify-center rounded-xs"
      onClick={switchColorTheme}
    >
      <div
        className="size-5 bg-gray-text"
        style={{ mask: maskValue, WebkitMask: maskValue }}
      />
    </button>
  );

  return (
    <div
      className="bg-background-secondary max-md:sticky max-md:top-0 max-md:rounded-b-sm max-md:drop-shadow-[0px_4px_2px_rgba(0,0,0,0.05)]"
      style={!isMobile ? undefined : { zIndex: zMobileTopBar }}
    >
      <div className="mx-auto max-w-306">
        <div className="flex items-center justify-between py-xl max-md:px-l max-md:py-s">
          {!isMobile ?
            <>
              <div className="flex items-center">
                <div className="flex w-52 items-center">{logoEl}</div>
                <div className="flex items-center gap-xl">
                  {links.map(({ key, label, to: src }) => {
                    const isMarkets = src === '/markets';
                    const showBadge = isMarkets && newMarketsCount > 0;
                    return (
                      <SmartLink
                        key={label}
                        className={NAV_LINK_CLASS}
                        navLink
                        data-onboarding={`nav-${label.toLowerCase()}`}
                        onClick={(e: MouseEvent) => onNavClick(e, label)}
                        to={src}
                      >
                        {NAV_LABELS[key]}
                        {showBadge && (
                          <span className="ml-xs text-purple-default">
                            · {newMarketsCount} new
                          </span>
                        )}
                      </SmartLink>
                    );
                  })}
                  {profile != null && isDemoAccount(profile) && (
                    <button
                      type="button"
                      className="btn-reset flex h-8 items-center justify-center rounded-xs px-m text-body-md font-medium whitespace-nowrap text-purple-default hover:text-purple-hover"
                      onClick={() => {
                        track('score_og_modal_shown');
                        setScoreOgOpen(true);
                      }}
                    >
                      🔥 Join as OG
                    </button>
                  )}
                  <SmartLink
                    className={NAV_LINK_CLASS}
                    navLink
                    onClick={(e: MouseEvent) => onNavClick(e, 'Help')}
                    to="/help"
                  >
                    Help
                  </SmartLink>
                </div>
              </div>
              <div className="flex items-center gap-2xl">
                {/* Wallet-bound affordance: hidden for walletless (SSO)
                    accounts — the modal itself is gated the same way. */}
                {profile?.address != null && (
                  <SanSwapPill
                    balance={sanBalance}
                    onClick={() => {
                      if (!guardWalletAction()) return;
                      track('swap_modal_opened', { source: 'header' });
                      setSwapOpen(true);
                    }}
                  />
                )}
                <div className="flex items-center gap-l">
                  {colorThemeButtonEl}
                  {profile && (
                    <div style={{ zIndex: zHeaderNotifications }}>
                      <Notifications />
                    </div>
                  )}
                  {profile && <ProfileMenu />}
                  {loginButtonEl}
                </div>
              </div>
            </>
          : <>
              {logoEl}
              {/* The ≤767 bar carries no nav, Help or profile entry: the
                  bottom nav pill owns navigation and the profile tab, /help
                  is reachable by URL and from the desktop header. Logged
                  out (the public market and KOL pages) the bell's slot holds the
                  Sign In button — no logged-out bar is designed. */}
              <div className="flex items-center gap-s">
                {colorThemeButtonEl}
                {profile && <Notifications />}
                {loginButtonEl}
              </div>
            </>
          }
        </div>
      </div>
      {swapOpen && profile?.address != null && (
        <SwapModal
          address={profile.address}
          onClose={() => setSwapOpen(false)}
        />
      )}
      {gateEl}
      {scoreOgOpen && <ScoreOgModal onClose={() => setScoreOgOpen(false)} />}
      {walletGuardModal}
    </div>
  );
}

// Desktop-only: the mobile treatment is undesigned, not deliberately
// excluded.
function SanSwapPill({
  balance,
  onClick,
}: {
  balance: bigint | undefined;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-reset flex h-8 items-center gap-s rounded-sm bg-background-main px-m text-body-md font-medium whitespace-nowrap"
    >
      <span className="text-gray">
        Available SAN:{' '}
        {balance === undefined ?
          '—'
        : formatPrice(Number(formatUnits(balance, 18)))}
      </span>
      <span className="text-gray-text">Swap</span>
      <ArrowUpDown size={16} className="text-gray-text" />
    </button>
  );
}
